import React, { useCallback, useMemo, useState } from 'react';

import { useWasteCharges } from '../../../../lib/hooks/useWasteCharges';
import { formatDateWithMonthName, formatSiteAddress } from './formatters';
import { ReactComponent as ArrowDownIcon } from '../../../Shared/svgs/arrow-triangle-down-magenta.svg';
import { ReactComponent as ArrowRightIcon } from '../../../Shared/svgs/arrow-triangle-right-magenta.svg';
import { ReactComponent as ApartmentsHouseIcon } from '../../../Shared/svgs/apartments-house-magenta.svg';
import { ReactComponent as PdfDownloadIcon } from '../../../Shared/svgs/pdf-dowload.svg';

import DownloadErrorModal, { DOWNLOAD_ERROR_MESSAGE } from './DownloadErrorModal';
import { downloadWasteInvoice } from '../../../../lib/api/waste-invoice';

const renderArrowIcon = (isExpanded) =>
    isExpanded ? (
        <ArrowDownIcon className="overweight-charges__arrow" />
    ) : (
        <ArrowRightIcon className="overweight-charges__arrow" />
    );

const getSiteWasteOverweights = (payload) => {
    const apiData = payload?.data ?? payload;

    if (Array.isArray(apiData?.siteWasteOverweights)) {
        return apiData.siteWasteOverweights;
    }
    if (Array.isArray(apiData)) {
        return apiData;
    }

    return [];
};

const formatOverweightCharge = (value, formatter) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? formatter.format(numericValue) : '-';
};

const formatOverweightAmount = (value) =>
    value !== undefined && value !== null && value !== '' ? `${value}kg` : '-';

export default function OverweightCharges() {
    const [isOpen, setIsOpen] = useState(true);
    const [expandedSites, setExpandedSites] = useState({});
    const [downloadErrorMessage, setDownloadErrorMessage] = useState('');
    const [isDownloadErrorOpen, setIsDownloadErrorOpen] = useState(false);
    const { data, loading, error } = useWasteCharges();

    const currencyFormatter = useMemo(
        () =>
            new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
            }),
        []
    );

    const customerDomainId = data?.data?.customerDomainId;

    const sites = useMemo(
        () =>
            getSiteWasteOverweights(data).map((site, index) => {
                const formattedAddress = formatSiteAddress(site?.addressesWithTypes?.[0], '');
                const charges = (site?.wasteOverweights ?? []).map((charge, chargeIndex) => ({
                    id: charge?.assetId ?? charge?.invoiceNumber ?? `${site?.siteId ?? index}-${chargeIndex}`,
                    invoiceNumber: charge?.invoiceNumber || '-',
                    wasteStream: charge?.assetTypeDescription || charge?.serviceTypeName || '-',
                    collectionDate: formatDateWithMonthName(charge?.nextCollectionDate),
                    overweightAmount: formatOverweightAmount(charge?.overweightAmount),
                    overweightCharge: formatOverweightCharge(charge?.overweightCharge, currencyFormatter),
                }));

                return {
                    id: String(site?.siteId ?? index),
                    name: formattedAddress,
                    charges,
                };
            }),
        [currencyFormatter, data]
    );

    const toggleSite = useCallback(
        (siteId) => setExpandedSites((prev) => ({ ...prev, [siteId]: !prev[siteId] })),
        []
    );

    const showDownloadError = useCallback(
        (message = DOWNLOAD_ERROR_MESSAGE) => {
            setDownloadErrorMessage(message);
            setIsDownloadErrorOpen(true);
        },
        []
    );

    const handleDownloadClick = useCallback(
        async (bill) => {
            if (!customerDomainId || !bill?.invoiceNumber) {
                console.warn('Missing data required to download waste invoice');
                showDownloadError();
                return;
            }

            try {
                const { blob, contentType, filename } = await downloadWasteInvoice({
                    customerDomainId,
                    invoiceNumber: bill.invoiceNumber,
                    documentFormat: 'pdf',
                });

                const url = URL.createObjectURL(new Blob([blob], { type: contentType }));
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
                setIsDownloadErrorOpen(false);
                setDownloadErrorMessage('');
            } catch (err) {
                console.error('Download failed!', err);
                showDownloadError();
            }
        },
        [customerDomainId, showDownloadError]
    );

    const renderContent = () => {
        if (loading) {
            return <div className="overweight-charges__empty">Loading overweight charges...</div>;
        }
        if (error) {
            return <div className="overweight-charges__empty">Unable to load overweight charges.</div>;
        }
        if (sites.length === 0) {
            return <div className="overweight-charges__empty">No overweight charges</div>;
        }

        return (
            <ul className="overweight-charges__sites">
                {sites.map((site) => {
                    const isSiteOpen = !!expandedSites[site.id];
                    const charges = site.charges || [];
                    const hasCharges = charges.length > 0;

                    return (
                        <li key={site.id} className="overweight-charges__site">
                            <button
                                type="button"
                                className="overweight-charges__site-toggle"
                                onClick={() => toggleSite(site.id)}
                                aria-expanded={isSiteOpen}
                            >
                                <div className="overweight-charges__site-header">
                                    <ApartmentsHouseIcon className="overweight-charges__site-icon" aria-hidden="true" />
                                    <span>{site.name}</span>
                                </div>
                                {renderArrowIcon(isSiteOpen)}
                            </button>

                            {isSiteOpen &&
                                (hasCharges ? (
                                    <div className="overweight-charges__table">
                                        <div className="overweight-charges__row overweight-charges__row--head">
                                            <div className="overweight-charges__cell">Invoice</div>
                                            <div className="overweight-charges__cell">Waste Stream</div>
                                            <div className="overweight-charges__cell">Collection Date</div>
                                            <div className="overweight-charges__cell">Overweight Amount</div>
                                            <div className="overweight-charges__cell">Overweight Charge</div>
                                            <div className="overweight-charges__cell overweight-charges__cell--download" />
                                        </div>

                                        {charges.map((charge) => (
                                            <div key={charge.id} className="overweight-charges__row">
                                                <div className="overweight-charges__cell">{charge.invoiceNumber}</div>
                                                <div className="overweight-charges__cell">{charge.wasteStream}</div>
                                                <div className="overweight-charges__cell">{charge.collectionDate}</div>
                                                <div className="overweight-charges__cell">{charge.overweightAmount}</div>
                                                <div className="overweight-charges__cell">{charge.overweightCharge}</div>
                                                <div className="overweight-charges__cell overweight-charges__cell--download">
                                                    <button
                                                        type="button"
                                                        className="overweight-charges__download"
                                                        onClick={() => handleDownloadClick(charge)}
                                                        disabled={!customerDomainId}
                                                    >
                                                        <PdfDownloadIcon className="overweight-charges__download-icon" aria-hidden="true" />
                                                        <span>PDF</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="overweight-charges__empty">No overweight charges</div>
                                ))}
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <div className="generic-details__block overweight-charges">
            <DownloadErrorModal
                open={isDownloadErrorOpen}
                message={downloadErrorMessage}
                onClose={() => setIsDownloadErrorOpen(false)}
            />

            <button
                type="button"
                className="overweight-charges__header"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
            >
                <h3>Overweight charges</h3>
                {renderArrowIcon(isOpen)}
            </button>

            {isOpen && renderContent()}
        </div>
    );
}
